import dayjs from 'dayjs'
import { Popover, TableColumnGroupType, Table } from 'antd'
import { Avatar } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { generateDatesArray } from 'screens/Reports/RiskAssessmentDashboard/screens/utils/utils'
import { getNominative } from 'common/utils'
import { Home, MonitoringReportRow, MonitoringCellData } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import MonitoringReportCellPopover from '../MonitoringReportCellPopover/MonitoringReportCellPopover'
import { Link } from 'react-router-dom'
import { FilterValues } from '../MonitoringReport/MonitoringReport'
import { formatData, generateQueryString, getReportPath } from '../../utils'

type DashboardTableProps = {
  dataSource?: MonitoringReportRow[]
  filterParams: FilterValues
  reportingId: string
}

const DashboardTable = ({
  dataSource,
  filterParams,
  reportingId,
}: DashboardTableProps) => {
  const datesArray = generateDatesArray(
    filterParams.rangePicker?.[0] || dayjs(),
    filterParams.rangePicker?.[1] || dayjs()
  )
  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const columns: TableColumnGroupType<MonitoringReportRow>[] = [
    {
      title: ' ',
      children: [
        {
          dataIndex: 'resident',
          title: (
            <SpacedColumnHeader>
              <BaseText weight="medium">{peopleNominative}</BaseText>
            </SpacedColumnHeader>
          ),
          key: 'resident',
          fixed: 'left',
          sorter: (a, b) =>
            a.resident.name?.first?.localeCompare(b.resident?.name?.first),
          width: 140,
          defaultSortOrder: 'ascend',
          render: (row: MonitoringReportRow['resident']) => (
            <Link
              rel="noopener noreferrer"
              target="_blank"
              to={`/residents/${row.id}/charts/${getReportPath(reportingId)}`}
            >
              <PersonCard>
                <Avatar url={row.picture} size={30} />
                <BaseText weight="bold">
                  {row.name?.first} {row.name?.last}
                </BaseText>
              </PersonCard>
            </Link>
          ),
        },
      ],
    },
    ...datesArray.map(({ month, days }) => ({
      title: (
        <YearContainer>
          <YearText level={4} weight="regular">
            {dayjs(month, 'MM/YY').format('MMMM YYYY')}
          </YearText>
        </YearContainer>
      ),
      children: days.map((date) => ({
        dataIndex: 'data',
        title: (
          <DateContainer>
            <BaseText weight="medium">
              {dayjs(date, 'DD/MM/YYYY').format('D')}
            </BaseText>
            <DayText weight="regular">
              {dayjs(date, 'DD/MM/YYYY').format('ddd')}
            </DayText>
          </DateContainer>
        ),
        key: 'data',
        render: (row: MonitoringCellData[], record: MonitoringReportRow) => {
          const cellData = row.find((data) => data.date === date)
          return (
            <MonitoringReportCell>
              <Popover
                placement="top"
                showArrow
                trigger="hover"
                content={
                  <MonitoringReportCellPopover
                    cellData={cellData}
                    reportingId={reportingId}
                  />
                }
              >
                <StyledLink
                  rel="noopener noreferrer"
                  target="_blank"
                  to={`/logs${generateQueryString({
                    resident: record.resident.id,
                    from: cellData?.date,
                    to: cellData?.date,
                    loggedBy: filterParams?.loggedBy || [],
                    categories: filterParams.categories || [],
                    // @ts-ignore
                    items: record.items || [],
                  })}`}
                >
                  <Text>
                    {cellData?.value ? formatData(cellData.value) : ''}
                  </Text>
                </StyledLink>
              </Popover>
            </MonitoringReportCell>
          )
        },
      })),
    })),
  ]

  return (
    <Table
      bordered
      dataSource={dataSource}
      columns={columns}
      scroll={{ x: 'max-content' }}
      pagination={{
        showSizeChanger: true,
        position: ['bottomCenter'],
      }}
    />
  )
}

const MonitoringReportCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color3};
  width: 40px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.status.primary};
  }
`

const YearContainer = styled.div`
  display: flex;
  margin-bottom: -12px;
`

const YearText = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const BaseText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DayText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const PersonCard = styled.div`
  width: 220px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 120px;
  }
`

const SpacedColumnHeader = styled.div`
  margin-left: 8px;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`

export default DashboardTable
